import React from 'react';
import { ShieldCheck, Sparkles, BadgeCheck, Landmark, ScanEye, UserCheck, ClipboardCheck, FileCheck, ClipboardList, RefreshCw, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { levelLabel, levelMeta, CREDENTIAL_LEVELS } from '../utils/credentialLevel';

/**
 * Credential-level pill (L3 / L4 / L5) — the production-authoritative level for a
 * programme, formatted for display. Level-coded so higher tiers stand out for
 * board/exec buyers (L5 = amber/gold, L4 = violet, L3 = indigo). Never hardcodes a
 * level: the value is always the programme's real `credential_level`.
 */
const LEVEL_PILL_CLS = {
  L3: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800',
  L4: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800',
  L5: 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700',
};
export const CredentialLevelBadge = ({ level, size = 'sm', withWord = true, className = '' }) => {
  const meta = levelMeta(level);
  const px = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  const ic = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <span
      data-testid="credential-level-badge"
      data-level={meta.code}
      title={`${meta.rqf} — ${meta.scqf} (Scotland) / ${meta.eqf} (Europe). Framed at this level to match the programme's depth; aligned to these frameworks for benchmarking, not a regulated qualification.`}
      className={`inline-flex items-center gap-1 font-semibold rounded-full border ${LEVEL_PILL_CLS[meta.code] || LEVEL_PILL_CLS.L3} ${px} ${className}`}
    >
      <Award className={ic} /> {withWord ? `${meta.label} Credential` : meta.label}
    </span>
  );
};

/**
 * Family-hub level progression strip — shows the distinct credential levels present
 * across a family's members (e.g. "Level 3 · Level 4 · Level 5"), with the levels the
 * family actually offers highlighted. A strong trust/upsell signal for board buyers
 * when an L5 rung sits at the top of a pathway. Derived purely from member data.
 */
export const CredentialLevelProgression = ({ members = [], className = '' }) => {
  const present = new Set(
    (members || []).map(m => levelMeta(m.credential_level).code)
  );
  if (present.size === 0) return null;
  return (
    <div data-testid="family-level-progression" className={`inline-flex items-center gap-1.5 flex-wrap ${className}`}>
      {CREDENTIAL_LEVELS.map((code, i) => {
        const has = present.has(code);
        return (
          <React.Fragment key={code}>
            {i > 0 && <span className="text-slate-300 dark:text-slate-600 text-xs">·</span>}
            <span
              data-testid={`family-level-${code}`}
              data-present={has ? 'true' : 'false'}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                has ? (LEVEL_PILL_CLS[code]) : 'bg-transparent text-slate-300 dark:text-slate-700 border-slate-200 dark:border-slate-800'
              }`}
            >
              {has && code === 'L5' && <Award className="w-3 h-3" />}{levelLabel(code)}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€' };
export const currencySymbol = (c) => CURRENCY_SYMBOLS[(c || 'GBP').toUpperCase()] || `${(c || 'GBP').toUpperCase()} `;

/**
 * D28 — "QA Verified" trust badge. Surfaced only when a programme has both
 * passed the automated D12 QA rubric AND received a valid human D13 SME sign-off.
 */
export const QAVerifiedBadge = ({ verified, size = 'sm', className = '' }) => {
  if (!verified) return null;
  const px = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  const ic = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <span
      data-testid="qa-verified-badge"
      className={`inline-flex items-center gap-1 font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 ${px} ${className}`}
      title="QA Verified — passed the automated quality rubric and signed off by a human reviewer"
    >
      <ShieldCheck className={ic} /> QA Verified
    </span>
  );
};

/**
 * D28 — entitlement-status / pricing pill. Shows the price for premium
 * programmes, otherwise "Included". Discovery-stage signalling only.
 */
export const ProgrammePricePill = ({ priceCents, isPremium, currency, sale, size = 'sm', className = '' }) => {
  const premium = isPremium || (priceCents && priceCents > 0);
  const px = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  if (premium) {
    const sym = currencySymbol(currency);
    if (sale && sale.sale_price_cents) {
      return (
        <span
          data-testid="programme-price-pill"
          className={`inline-flex items-center gap-1.5 font-semibold rounded-full bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800 ${px} ${className}`}
        >
          <span className="line-through opacity-60" data-testid="programme-price-original">{sym}{(sale.original_price_cents / 100).toFixed(0)}</span>
          <span data-testid="programme-price-sale">{sym}{(sale.sale_price_cents / 100).toFixed(0)}</span>
          <span className="rounded-full bg-orange-600 text-white text-[9px] px-1.5 py-0.5 leading-none">-{sale.percent}%</span>
        </span>
      );
    }
    return (
      <span
        data-testid="programme-price-pill"
        className={`inline-flex items-center font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 ${px} ${className}`}
      >
        {sym}{(priceCents / 100).toFixed(0)} · Premium
      </span>
    );
  }
  return (
    <span
      data-testid="programme-included-pill"
      className={`inline-flex items-center font-semibold rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800 ${px} ${className}`}
    >
      Included
    </span>
  );
};

/**
 * Source-provenance / trust pill for PUBLIC programme cards + detail (Item 3).
 * Buyer-friendly wording DERIVED from the programme's grounding_provenance.tier
 * (lowest-trust-source-wins). Internal → bespoke signalling; authoritative/OGL →
 * neutral grounding wording with NO endorsement/accreditation claim. Renders
 * nothing when a programme has no classified sources. Never exposes filenames.
 */
const PROVENANCE_BUYER = {
  internal: {
    label: 'Bespoke — built from expert materials',
    cls: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800',
    title: 'Bespoke programme built from the publisher’s own expert source materials.',
  },
  authoritative: {
    label: 'Grounded in authoritative sources',
    cls: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800',
    title: 'Grounded in recognised authoritative sources. This is not an endorsement or accreditation.',
  },
  ogl: {
    label: 'Grounded in official sources',
    cls: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    title: 'Grounded in official UK government (Open Government Licence) sources.',
  },
};

export const ProgrammeProvenancePill = ({ provenance, size = 'sm', className = '' }) => {
  const tier = provenance?.tier;
  const meta = tier && PROVENANCE_BUYER[tier];
  if (!meta) return null;
  const px = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  const ic = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const Icon = tier === 'internal' ? Sparkles : (tier === 'authoritative' ? BadgeCheck : Landmark);
  return (
    <span
      data-testid="programme-provenance-pill"
      data-provenance-tier={tier}
      title={meta.title}
      className={`inline-flex items-center gap-1 font-semibold rounded-full border ${meta.cls} ${px} ${className}`}
    >
      <Icon className={ic} /> {meta.label}
    </span>
  );
};

/**
 * Accurate, buyer-facing ASSESSMENT badge derived server-side from the
 * programme's real assessment model (vision capstone / human-confirmed project
 * assignment / included assignment / capstone). Never fabricated — renders
 * nothing when the backend returns no badge. Replaces the previously hard-coded
 * "Vision-graded capstone" so Yellow/Green/Black each show an accurate signal.
 */
const ASSESSMENT_META = {
  vision_capstone: {
    icon: ScanEye,
    cls: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-950/40 dark:text-fuchsia-300 dark:border-fuchsia-800',
    title: 'This credential is assessed with a real, vision-graded scenario.',
  },
  assignment_mandatory: {
    icon: UserCheck,
    cls: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800',
    title: 'The credential requires a project assignment, AI-marked and confirmed by a human assessor.',
  },
  assignment_optional: {
    icon: ClipboardCheck,
    cls: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800',
    title: 'Includes a project assignment, AI-marked and confirmed by a human assessor.',
  },
  capstone: {
    icon: FileCheck,
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
    title: 'Assessed with an integrative capstone.',
  },
};

export const AssessmentBadge = ({ badge, size = 'sm', className = '' }) => {
  const meta = badge && ASSESSMENT_META[badge.kind];
  if (!meta) return null;
  const px = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  const ic = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const Icon = meta.icon;
  return (
    <span
      data-testid="assessment-badge"
      data-assessment-kind={badge.kind}
      title={meta.title}
      className={`inline-flex items-center gap-1 font-semibold rounded-full border ${meta.cls} ${px} ${className}`}
    >
      <Icon className={ic} /> {badge.label}
    </span>
  );
};

/**
 * OWNER-ONLY readiness hint — flags programmes that have no Programme Assignment
 * configured yet. Informational (not an error): an assignment is optional unless
 * a programme's policy is `mandatory`. Renders nothing once a definition exists.
 * Driven by the list serializer's `has_assignment_definition` / `assignment_policy`.
 */
export const AssignmentSetupBadge = ({ hasDefinition, policy, size = 'sm', className = '' }) => {
  if (hasDefinition) return null;
  const px = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  const ic = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  // mandatory + no definition is credential-blocking → amber; otherwise neutral slate.
  const blocking = policy === 'mandatory';
  const cls = blocking
    ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'
    : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
  return (
    <span
      data-testid="assignment-setup-badge"
      data-assignment-blocking={blocking ? 'true' : 'false'}
      title={blocking
        ? 'This programme requires a project assignment (policy: mandatory) but none is set up yet — learners cannot earn the credential until you add one.'
        : 'No project assignment is set up for this programme. This is optional unless you set the assignment policy to mandatory.'}
      className={`inline-flex items-center gap-1 font-semibold rounded-full border ${cls} ${px} ${className}`}
    >
      <ClipboardList className={ic} /> {blocking ? 'Assignment required' : 'No assignment set up'}
    </span>
  );
};

/**
 * OWNER-ONLY subtle "needs re-sign-off" hint. Shown when a programme was SME
 * signed off but the sign-off has since gone STALE (content edited → version
 * bumped → previous sign-off no longer matches the current version). Renders
 * nothing for never-signed-off or currently-valid programmes.
 * Driven by the serializer's `sme_signoff` (status) + `sme_signoff_valid`.
 */
export const StaleSignoffHint = ({ programme, size = 'sm', className = '' }) => {
  const so = programme?.sme_signoff || {};
  const stale = so.status === 'signed_off' && !programme?.sme_signoff_valid;
  if (!stale) return null;
  const px = size === 'lg' ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  const ic = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <span
      data-testid="stale-signoff-hint"
      title={`This programme was signed off at an earlier version (v${so.programme_version ?? '?'}) but has since been edited (now v${programme?.version ?? '?'}). Re-sign-off is needed before it can stay QA Verified / be re-published.`}
      className={`inline-flex items-center gap-1 font-semibold rounded-full border bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800 ${px} ${className}`}
    >
      <RefreshCw className={ic} /> Needs re-sign-off
    </span>
  );
};

/* CPFPSI trust badge — links to the Professional Standards Institute (credential-trust conversion asset). */
export const CPFPSITrustBadge = ({ variant = 'card', className = '' }) => {
  if (variant === 'inline') {
    return (
      <Link
        to="/professional-standards-institute"
        data-testid="cpfpsi-trust-badge-inline"
        className={`inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900 rounded-full px-3 py-1 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors ${className}`}
      >
        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" /> Issued under the CPDForge Professional Standards Institute
      </Link>
    );
  }
  return (
    <Link
      to="/professional-standards-institute"
      data-testid="cpfpsi-trust-badge"
      className={`group flex items-start gap-3 rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/30 px-4 py-3 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors ${className}`}
    >
      <div className="w-8 h-8 rounded-lg bg-indigo-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
        <ShieldCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-900 dark:text-white leading-snug">CPDForge Professional Standards Institute</p>
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-snug mt-0.5">
          Credentials are grounded, quality-assured and publicly verifiable under the CPFPSI framework.{' '}
          <span className="text-indigo-600 dark:text-indigo-400 font-semibold group-hover:underline">Learn how &rarr;</span>
        </p>
      </div>
    </Link>
  );
};
